import { useEffect, useState } from "react";
import { message } from "antd";
import styled from "styled-components";
import { countryTaxConfigAPI, type CountryTaxConfigRow } from "@/api/countryTaxConfig";
import { DynamicModal } from "@/shared/components";
import { getApiErrorMessage } from "@/shared/utils/apiError";
import { phoneCountries } from "@/shared/utils/phoneCountries";
import { businessToday } from "../utils/businessDate";
import { formatPercent } from "../utils/countryConfig";

interface Props {
	row: CountryTaxConfigRow | null;
	onClose: () => void;
	onEnded: (row: CountryTaxConfigRow) => void;
}

const today = businessToday;

/** Sets valid_to on the current row (rare). Creators in the country cannot be sent to Finance after that date. */
export const EndRateModal = ({ row, onClose, onEnded }: Props) => {
	const [validTo, setValidTo] = useState(today());
	const [notes, setNotes] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (row) {
			setValidTo(today());
			setNotes("");
			setError(null);
		}
	}, [row]);

	const submit = async () => {
		if (!row) return;
		if (validTo < today()) return setError("Valid to must be today or later.");
		if (notes.trim().length < 10) return setError("Please explain the change (at least 10 characters).");
		setSaving(true);
		try {
			const ended = await countryTaxConfigAPI.end(row.id, validTo, notes.trim());
			message.success("Rate ended.");
			onEnded(ended);
		} catch (e) {
			setError(getApiErrorMessage(e, "Unable to end the rate"));
		} finally {
			setSaving(false);
		}
	};

	const country = row ? (phoneCountries.find((c) => c.iso === row.countryCode)?.name ?? row.countryCode) : "";

	return (
		<DynamicModal
			open={Boolean(row)}
			onCancel={onClose}
			title={`End the current ${row?.taxName ?? ""} rate for ${country}`}
			description={`${formatPercent(row?.effectiveRate ?? null)} stops applying from the date below. From then, creators in ${country} cannot be sent to Finance until a new row exists.`}
			variant="danger"
			confirmText="End rate"
			cancelText="Back"
			onConfirm={() => void submit()}
			confirmDisabled={saving}
			loading={saving}
		>
			<Label htmlFor="end-valid-to">Valid to</Label>
			<Input
				id="end-valid-to"
				type="date"
				min={today()}
				value={validTo}
				onChange={(e) => setValidTo(e.target.value)}
			/>
			<Label htmlFor="end-notes">Reason (audit note)</Label>
			<TextArea id="end-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
			{error ? <Error role="alert">{error}</Error> : null}
		</DynamicModal>
	);
};

const Label = styled.label`
	display: block;
	width: 100%;
	margin: 8px 0 6px;
	color: #303030;
	font-size: 14px;
	font-weight: 600;
`;
const Input = styled.input`
	box-sizing: border-box;
	width: 100%;
	height: 40px;
	padding: 0 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	font: inherit;
`;
const TextArea = styled.textarea`
	box-sizing: border-box;
	width: 100%;
	padding: 8px 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	font: inherit;
	resize: vertical;
`;
const Error = styled.p`
	margin: 8px 0 0;
	color: #b3261e;
	font-size: 13px;
`;
